import type { DeviceVersion } from "../../schemas/commonSubSchemas/deviceVersion.js";
import { DeviceTypeConfigurations } from "../DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { GENERATION_CODES } from "../DeviceTypeConfiguration/types.js";
import {
    BATTERY_LEVEL,
    BLOCK_VOLTAGE_LEVELS,
    type BatteryLevelThresholds,
    SEED_DEVICE_STATS_BATTERY_LEVELS,
    SPROUT_VOLTAGE_LEVELS_MICRO,
} from "./batteryLevels.js";

/** The battery fields of a device document, a third-party device document, or a dashboard device model. */
export type BatteryLevelSource = {
    version?: Pick<DeviceVersion, "generation" | "productLine">;
    DeviceType?: string;
    deviceType?: string;
    gen4NetworkInfo?: { lastBatteryVoltageReading?: { value?: number } };
    sproutNetworkInfo?: { batteryVoltage?: number };
    device_stats?: { batt?: number };
    batteryIndicator?: number;
};

function findLevelFromValue(levels: BatteryLevelThresholds, value: number | undefined): BATTERY_LEVEL {
    // 0 is the unset default and null an empty reading; neither is a level.
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
        return BATTERY_LEVEL.UNKNOWN;
    }
    return levels.find(({ max }) => value < max)?.level ?? BATTERY_LEVEL.FULL;
}

function resolveBattery(device: BatteryLevelSource): {
    reading: number | undefined;
    levels: BatteryLevelThresholds | null;
} {
    const config =
        DeviceTypeConfigurations[(device.deviceType ?? device.DeviceType) as keyof typeof DeviceTypeConfigurations];
    // A first-party device that has not reported its version yet takes the generation of its type
    const generation =
        device.version?.generation ?? (config?.isVerdiDevice ? GENERATION_CODES[config.generation] : undefined);

    if (generation !== undefined) {
        if (generation === GENERATION_CODES.gen4) {
            return { reading: device.gen4NetworkInfo?.lastBatteryVoltageReading?.value, levels: BLOCK_VOLTAGE_LEVELS };
        }
        if (generation === GENERATION_CODES.sprout) {
            const productLine = device.version?.productLine?.toLowerCase();
            return {
                reading: device.sproutNetworkInfo?.batteryVoltage,
                levels:
                    productLine === "block"
                        ? BLOCK_VOLTAGE_LEVELS
                        : productLine === "micro"
                          ? SPROUT_VOLTAGE_LEVELS_MICRO
                          : null,
            };
        }
        if (generation === GENERATION_CODES.seed) {
            return { reading: device.device_stats?.batt, levels: SEED_DEVICE_STATS_BATTERY_LEVELS };
        }
        return { reading: undefined, levels: null };
    }

    if (config?.isVerdiDevice === false) {
        return { reading: device.batteryIndicator, levels: config.batteryIndicatorLevels };
    }

    // Legacy Sensoterra probes carry no version and report through device_stats.batt
    return { reading: device.device_stats?.batt, levels: SEED_DEVICE_STATS_BATTERY_LEVELS };
}

/** The raw battery value the level is read from: volts, or the Seed/legacy Sensoterra device_stats.batt. */
export function getBatteryReading(device: BatteryLevelSource): number | undefined {
    return resolveBattery(device).reading;
}

export function getBatteryLevel(device: BatteryLevelSource): BATTERY_LEVEL {
    const { reading, levels } = resolveBattery(device);
    return levels ? findLevelFromValue(levels, reading) : BATTERY_LEVEL.UNKNOWN;
}
